import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Single source of truth for the client on the user's subscription state.
 *
 * IMPORTANT: this must always match the backend's `is_paid()` and
 * `/subscription/status` — write-gates, banners, and paywall copy on every
 * screen (Home, Coach, Journal, Weekly Reflection, Saved Wisdom, Growth,
 * Profile, Analyzer) call this so they can never disagree.
 *
 * Returns:
 *   plan             — "free" | "pro"
 *   accessSource     — "trial" | "gift" | "personal_subscription" | "none"
 *   expiresAt        — Date | null (whichever expiry applies to the source)
 *   isTrialActive    — trial with expiry in the future
 *   isTrialExpired   — trial with expiry in the past
 *   isGift           — gifted Pro (regardless of expiry — expired gifts also flow through here; UI treats them as free)
 *   isGiftActive     — gifted Pro not yet expired
 *   isPaidSubscriber — paid personal Stripe subscription, not expired
 *   isPaid           — any active paid state (trial OR gift OR paid)
 *   isFreeOnly       — no active access at all
 */
export default function useSubscriptionStatus() {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user) {
      return {
        plan: "free",
        accessSource: "none",
        expiresAt: null,
        isTrialActive: false,
        isTrialExpired: false,
        isGift: false,
        isGiftActive: false,
        isPaidSubscriber: false,
        isPaid: false,
        isFreeOnly: true,
      };
    }
    const plan = user.subscription_tier || user.plan || "free";
    const rawSource = user.access_source;
    const accessSource =
      rawSource ||
      (plan !== "free" ? "personal_subscription" : "none");
    const expiresRaw =
      user.access_expires_at || user.subscription_expires_at || null;
    let expiresAt = null;
    let notExpired = true;
    if (expiresRaw) {
      try {
        expiresAt = new Date(expiresRaw);
        notExpired = expiresAt.getTime() > Date.now();
      } catch {
        expiresAt = null;
        notExpired = false;
      }
    }

    const isTrialActive = accessSource === "trial" && notExpired;
    const isTrialExpired = accessSource === "trial" && !notExpired;
    const isGiftActive = accessSource === "gift" && notExpired;
    const isPaidSubscriber =
      accessSource === "personal_subscription" &&
      // legacy paid grants without expiry are valid; only treat as expired
      // if we have an expiry timestamp and it's in the past.
      (!expiresRaw || notExpired);

    const isPaid = isTrialActive || isGiftActive || isPaidSubscriber;
    const isFreeOnly = !isPaid;

    return {
      plan,
      accessSource,
      expiresAt,
      isTrialActive,
      isTrialExpired,
      isGift: accessSource === "gift",
      isGiftActive,
      isPaidSubscriber,
      isPaid,
      isFreeOnly,
    };
  }, [user]);
}
